/** SUNTRONIX ONE — Trade-In, Buyback Credit & Invoice Offset (Phase 1).
 *
 *  India-located hardware buybacks become audited credits held against
 *  each customer's Suntronix account.  Credits can be applied against
 *  monthly ITAM statements / invoices, reducing revenue leakage while
 *  giving the customer a transparent lifecycle exit path.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Recycle, Plus, RefreshCcw, CheckCircle2, XCircle, Send,
  ArrowLeftRight, Coins, Trash2, Wallet, FileText,
} from "lucide-react";

const STATUS_TONE = {
  DRAFT:      "bg-slate-100 text-slate-700",
  SUBMITTED:  "bg-sky-100 text-sky-800",
  APPROVED:   "bg-emerald-100 text-emerald-800",
  REJECTED:   "bg-rose-100 text-rose-800",
  CREDITED:   "bg-indigo-100 text-indigo-800",
  CANCELLED:  "bg-slate-200 text-slate-500 line-through",
};

const TABS = [
  { id: "buybacks",  label: "Buybacks" },
  { id: "ledger",    label: "Credit ledger" },
  { id: "apply",     label: "Apply credit" },
];

const fmt = (n, ccy = "INR") =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: ccy || "INR", maximumFractionDigits: 0 }).format(n || 0);

const EMPTY_LINE = { description: "", serial: "", category: "",
                     condition: "B", quantity: 1, proposed_amount: 0, notes: "" };


export default function Buybacks() {
  const { user } = useAuth();
  const isSuntronix = !user?.master_company_id;
  const [tab, setTab] = useState("buybacks");
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [masters, setMasters] = useState([]);
  const [busy, setBusy] = useState(false);
  const [selectedMc, setSelectedMc] = useState("");
  const [ledger, setLedger] = useState({ items: [], balance: 0 });
  const [showCreate, setShowCreate] = useState(false);
  const [showApprove, setShowApprove] = useState(null);   // buyback obj
  const [showApply, setShowApply] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const [s, list, m] = await Promise.all([
        api.get("/buybacks/summary"),
        api.get("/buybacks", { params: { limit: 100 } }),
        isSuntronix ? api.get("/master-companies") : Promise.resolve({ data: { items: [] } }),
      ]);
      setSummary(s.data);
      setRows(list.data.items || []);
      setMasters(m.data.items || []);
      if (!selectedMc && !isSuntronix && user?.master_company_id) {
        setSelectedMc(user.master_company_id);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Load failed");
    } finally { setBusy(false); }
  }, [isSuntronix, selectedMc, user]);

  useEffect(() => { load(); }, [load]);

  const loadLedger = useCallback(async () => {
    if (!selectedMc) return;
    try {
      const { data } = await api.get(`/buybacks/credit-ledger/${selectedMc}`);
      setLedger(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load ledger");
    }
  }, [selectedMc]);

  useEffect(() => {
    if (tab === "ledger" && selectedMc) loadLedger();
  }, [tab, selectedMc, loadLedger]);

  const submitBuyback = async (id) => {
    try {
      await api.post(`/buybacks/${id}/submit`);
      toast.success("Buyback submitted for valuation");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Submit failed"); }
  };

  const rejectBuyback = async (id) => {
    const reason = window.prompt("Reason for rejection?");
    if (!reason) return;
    try {
      await api.post(`/buybacks/${id}/reject`, { reason });
      toast.success("Buyback rejected");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Reject failed"); }
  };

  const cancelBuyback = async (id) => {
    if (!window.confirm("Cancel this buyback?")) return;
    try {
      await api.post(`/buybacks/${id}/cancel`);
      toast.success("Buyback cancelled");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Cancel failed"); }
  };

  const issueCredit = async (id) => {
    try {
      const { data } = await api.post(`/buybacks/${id}/issue-credit`);
      toast.success(`Credit issued: ${fmt(data.ledger_entry?.amount ?? 0)}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Credit failed"); }
  };

  return (
    <div className="space-y-6 max-w-[1600px]" data-testid="buyback-page">
      {/* ---------- Header ---------- */}
      <div className="flex flex-wrap items-end gap-4">
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-semibold tracking-[0.14em] uppercase text-slate-500">
            Commercial · Trade-In
          </div>
          <h1 className="text-3xl font-heading font-semibold text-slate-900 tracking-tight mt-1 flex items-center gap-2">
            <Recycle className="h-6 w-6 text-slate-700" strokeWidth={1.6} />
            Buyback Credit &amp; Invoice Offset
          </h1>
          <p className="text-sm text-slate-600 mt-1.5 max-w-2xl">
            India-located hardware buybacks become audited credits against ITAM invoices.
            Every credit movement is signed, sourced and applied with full audit trail.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={load} disabled={busy} className="h-9"
            data-testid="buyback-refresh">
            <RefreshCcw className={"h-4 w-4 mr-1.5 " + (busy ? "animate-spin" : "")} strokeWidth={1.6} />
            Refresh
          </Button>
        </div>
      </div>

      {/* ---------- KPIs ---------- */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Kpi label="Pending valuation" value={summary.counts?.SUBMITTED || 0}
            tone="sky" icon={Send} testId="kpi-submitted" />
          <Kpi label="Approved" value={summary.counts?.APPROVED || 0}
            tone="emerald" icon={CheckCircle2} testId="kpi-approved" />
          <Kpi label="Credits issued" value={fmt(summary.total_credited_value)}
            tone="indigo" icon={Coins} testId="kpi-credited" />
          <Kpi label="Outstanding balance" value={fmt(summary.outstanding_credit_balance)}
            tone="fuchsia" icon={Wallet} testId="kpi-balance" />
        </div>
      )}

      {/* ---------- Tabs ---------- */}
      <div className="border-b border-slate-200 flex items-end gap-1 flex-wrap">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)}
            data-testid={`buyback-tab-${t.id}`}
            className={
              "h-9 px-4 text-[13px] font-medium border-b-2 transition-colors " +
              (tab === t.id
                ? "border-slate-900 text-slate-900"
                : "border-transparent text-slate-500 hover:text-slate-800")
            }>
            {t.label}
          </button>
        ))}
      </div>

      {/* ================================================================ */}
      {/* Buybacks tab                                                     */}
      {/* ================================================================ */}
      {tab === "buybacks" && (
        <div className="space-y-3">
          <div className="flex items-center justify-end">
            <Button onClick={() => setShowCreate(true)}
              data-testid="buyback-new"
              className="h-9 bg-slate-900 text-white hover:bg-slate-800">
              <Plus className="h-4 w-4 mr-1.5" strokeWidth={1.8} />
              New buyback
            </Button>
          </div>
          <div className="bg-white border border-slate-200 rounded-md divide-y">
            {rows.length === 0 ? (
              <div className="p-8 text-center text-sm text-slate-500">
                No buybacks yet. Start a trade-in intake to earn credit for retired hardware.
              </div>
            ) : rows.map((r) => (
              <BuybackRow key={r.id} row={r}
                masters={masters}
                isSuntronix={isSuntronix}
                onSubmit={() => submitBuyback(r.id)}
                onApprove={() => setShowApprove(r)}
                onReject={() => rejectBuyback(r.id)}
                onCancel={() => cancelBuyback(r.id)}
                onIssueCredit={() => issueCredit(r.id)} />
            ))}
          </div>
        </div>
      )}

      {/* ================================================================ */}
      {/* Ledger tab                                                       */}
      {/* ================================================================ */}
      {tab === "ledger" && (
        <div className="space-y-3">
          {isSuntronix && (
            <div className="bg-white border border-slate-200 rounded-md p-3 flex items-center gap-2">
              <span className="text-[11px] uppercase tracking-[0.12em] text-slate-500">Customer:</span>
              <Select value={selectedMc} onValueChange={setSelectedMc}>
                <SelectTrigger className="h-9 w-80" data-testid="ledger-customer-select">
                  <SelectValue placeholder="Select customer…" />
                </SelectTrigger>
                <SelectContent>
                  {masters.map((m) => (
                    <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button variant="outline" onClick={loadLedger} className="h-9 ml-1"
                disabled={!selectedMc}>
                <RefreshCcw className="h-4 w-4 mr-1.5" /> Reload
              </Button>
              <div className="ml-auto text-right">
                <div className="text-[10px] uppercase tracking-[0.12em] text-slate-500">Available credit</div>
                <div className="text-2xl font-heading font-semibold text-emerald-700"
                  data-testid="ledger-balance">
                  {fmt(ledger.balance)}
                </div>
              </div>
            </div>
          )}
          {!selectedMc ? (
            <div className="bg-white border border-slate-200 rounded-md p-8 text-center text-sm text-slate-500">
              Pick a customer to view their credit ledger.
            </div>
          ) : (
            <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-[10px] uppercase text-slate-500 tracking-wider">
                  <tr>
                    <th className="text-left px-3 py-2">Date</th>
                    <th className="text-left px-3 py-2">Type</th>
                    <th className="text-left px-3 py-2">Description</th>
                    <th className="text-right px-3 py-2">Amount</th>
                    <th className="text-right px-3 py-2">Balance</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {ledger.items.length === 0 ? (
                    <tr><td colSpan={5} className="text-center py-6 text-slate-500">
                      No ledger entries for this customer yet.
                    </td></tr>
                  ) : ledger.items.map((e) => (
                    <tr key={e.id} data-testid={`ledger-row-${e.id}`}>
                      <td className="px-3 py-2 text-slate-500 whitespace-nowrap">
                        {(e.created_at || "").slice(0, 10)}
                      </td>
                      <td className="px-3 py-2">
                        <LedgerBadge type={e.entry_type} />
                      </td>
                      <td className="px-3 py-2 text-slate-800">{e.description}</td>
                      <td className={"px-3 py-2 text-right font-medium " + (Number(e.amount) < 0 ? "text-rose-700" : "text-emerald-700")}>
                        {Number(e.amount) < 0 ? "−" : "+"}{fmt(Math.abs(Number(e.amount) || 0), e.currency)}
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-slate-700">
                        {fmt(e.running_balance, e.currency)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ================================================================ */}
      {/* Apply credit tab                                                 */}
      {/* ================================================================ */}
      {tab === "apply" && (
        <ApplyCreditPanel
          masters={masters}
          isSuntronix={isSuntronix}
          defaultMc={selectedMc || user?.master_company_id || ""}
          onDone={() => { load(); loadLedger(); toast.success("Credit applied"); }} />
      )}

      {/* ---------- Dialogs ---------- */}
      {showCreate && (
        <CreateBuybackDialog
          masters={masters}
          isSuntronix={isSuntronix}
          defaultMc={user?.master_company_id || ""}
          onCancel={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); load(); }} />
      )}
      {showApprove && (
        <ApproveBuybackDialog
          buyback={showApprove}
          onCancel={() => setShowApprove(null)}
          onDone={() => { setShowApprove(null); load(); }} />
      )}
    </div>
  );
}


/* ---------------------------------------------------------------- KPI */
function Kpi({ label, value, tone = "slate", icon: Icon, testId }) {
  const toneMap = {
    slate:   "text-slate-900",
    sky:     "text-sky-700",
    emerald: "text-emerald-700",
    indigo:  "text-indigo-700",
    fuchsia: "text-fuchsia-700",
  };
  return (
    <div className="bg-white border border-slate-200 rounded-md p-3.5" data-testid={testId}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500">{label}</div>
        {Icon && <Icon className="h-4 w-4 text-slate-400" strokeWidth={1.6} />}
      </div>
      <div className={"text-xl font-heading font-semibold mt-1 " + (toneMap[tone] || toneMap.slate)}>
        {value}
      </div>
    </div>
  );
}


function LedgerBadge({ type }) {
  const tone = {
    EARNED:     "bg-emerald-100 text-emerald-800",
    APPLIED:    "bg-indigo-100 text-indigo-800",
    EXPIRED:    "bg-slate-200 text-slate-600",
    ADJUSTMENT: "bg-amber-100 text-amber-900",
  }[type] || "bg-slate-100 text-slate-700";
  return (
    <span className={"h-5 px-2 rounded-full text-[10px] font-medium uppercase tracking-[0.1em] inline-flex items-center " + tone}>
      {type}
    </span>
  );
}


/* ---------------------------------------------------------------- Row */
function BuybackRow({ row, masters, isSuntronix,
                     onSubmit, onApprove, onReject, onCancel, onIssueCredit }) {
  const mcName = masters.find((m) => m.id === row.master_company_id)?.name;
  return (
    <div className="p-4 flex flex-wrap items-start gap-4 hover:bg-slate-50/50"
      data-testid={`buyback-row-${row.code}`}>
      <div className="h-9 w-9 rounded-md bg-slate-900 text-white grid place-items-center shrink-0">
        <Recycle className="h-4 w-4" strokeWidth={1.6} />
      </div>
      <div className="flex-1 min-w-[260px]">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="text-[14px] font-semibold text-slate-900 font-mono">{row.code}</div>
          <span className={"h-5 px-2 rounded-full text-[10px] font-medium uppercase tracking-[0.1em] " + STATUS_TONE[row.status]}>
            {row.status}
          </span>
          {mcName && <span className="text-[12px] text-slate-500">{mcName}</span>}
        </div>
        <div className="text-[12px] text-slate-600 mt-1">
          {row.items?.length || 0} item(s) · Proposed: <span className="font-semibold text-slate-900">{fmt(row.total_proposed, row.currency)}</span>
          {row.status === "APPROVED" || row.status === "CREDITED" ? (
            <> · Approved: <span className="font-semibold text-emerald-700">{fmt(row.total_approved, row.currency)}</span></>
          ) : null}
        </div>
        {row.rejection_reason && (
          <div className="text-[11px] text-rose-700 mt-1">Rejection: {row.rejection_reason}</div>
        )}
        {row.notes && (
          <div className="text-[11px] text-slate-500 mt-1">{row.notes}</div>
        )}
      </div>
      <div className="flex items-center gap-1.5 flex-wrap">
        {row.status === "DRAFT" && (
          <>
            <Button variant="outline" size="sm" className="h-8" onClick={onSubmit}
              data-testid={`buyback-submit-${row.code}`}>
              <Send className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.6} /> Submit
            </Button>
            <Button variant="ghost" size="sm" className="h-8 text-slate-500"
              onClick={onCancel} data-testid={`buyback-cancel-${row.code}`}>
              <Trash2 className="h-3.5 w-3.5" strokeWidth={1.6} />
            </Button>
          </>
        )}
        {row.status === "SUBMITTED" && isSuntronix && (
          <>
            <Button size="sm" className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white"
              onClick={onApprove} data-testid={`buyback-approve-${row.code}`}>
              <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.6} /> Approve
            </Button>
            <Button variant="outline" size="sm" className="h-8 text-rose-700"
              onClick={onReject} data-testid={`buyback-reject-${row.code}`}>
              <XCircle className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.6} /> Reject
            </Button>
          </>
        )}
        {row.status === "APPROVED" && isSuntronix && (
          <Button size="sm" className="h-8 bg-indigo-600 hover:bg-indigo-700 text-white"
            onClick={onIssueCredit} data-testid={`buyback-credit-${row.code}`}>
            <Coins className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.6} /> Issue credit
          </Button>
        )}
      </div>
    </div>
  );
}


/* ---------------------------------------------------- Create dialog */
function CreateBuybackDialog({ masters, isSuntronix, defaultMc,
                                onCancel, onCreated }) {
  const [mc, setMc] = useState(defaultMc);
  const [items, setItems] = useState([{ ...EMPTY_LINE }]);
  const [expectedDate, setExpectedDate] = useState("");
  const [customerRef, setCustomerRef] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const total = useMemo(() =>
    items.reduce((s, i) => s + (Number(i.proposed_amount) || 0) * (Number(i.quantity) || 1), 0),
    [items]);

  const updateItem = (idx, key, val) => {
    setItems((arr) => arr.map((it, i) => i === idx ? { ...it, [key]: val } : it));
  };

  const save = async () => {
    if (!mc) { toast.error("Pick a customer"); return; }
    if (items.length === 0 || !items[0].description) {
      toast.error("Add at least one item"); return;
    }
    setSaving(true);
    try {
      await api.post("/buybacks", {
        master_company_id: mc,
        currency: "INR",
        items: items.map((it) => ({
          description: it.description,
          serial: it.serial || null,
          category: it.category || null,
          condition: it.condition || "B",
          quantity: Number(it.quantity) || 1,
          proposed_amount: Number(it.proposed_amount) || 0,
          notes: it.notes || null,
        })),
        expected_collection_date: expectedDate || null,
        customer_reference: customerRef || null,
        notes: notes || null,
      });
      toast.success("Buyback draft created");
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Create failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onCancel()}>
      <DialogContent className="max-w-3xl" data-testid="buyback-create-dialog">
        <DialogHeader><DialogTitle>New buyback intake</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          {isSuntronix && (
            <div>
              <div className="text-[10px] uppercase tracking-[0.12em] text-slate-500 mb-1">Customer *</div>
              <Select value={mc} onValueChange={setMc}>
                <SelectTrigger className="h-9" data-testid="buyback-mc-select">
                  <SelectValue placeholder="Select customer…" />
                </SelectTrigger>
                <SelectContent>
                  {masters.map((m) => (
                    <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-[0.12em] text-slate-500 mb-1">Expected collection</div>
              <Input type="date" value={expectedDate} onChange={(e) => setExpectedDate(e.target.value)} />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.12em] text-slate-500 mb-1">Customer reference</div>
              <Input value={customerRef} onChange={(e) => setCustomerRef(e.target.value)}
                placeholder="Purchase order / RFC" />
            </div>
          </div>
          <div className="border border-slate-200 rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[10px] uppercase text-slate-500 tracking-wider">
                <tr>
                  <th className="text-left px-2 py-1.5">Item</th>
                  <th className="text-left px-2 py-1.5">Serial</th>
                  <th className="text-left px-2 py-1.5">Cond.</th>
                  <th className="text-right px-2 py-1.5 w-16">Qty</th>
                  <th className="text-right px-2 py-1.5 w-32">Amount (₹)</th>
                  <th className="w-8" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.map((it, idx) => (
                  <tr key={idx}>
                    <td className="px-1 py-1">
                      <Input value={it.description}
                        onChange={(e) => updateItem(idx, "description", e.target.value)}
                        placeholder="MacBook Pro 14 M2, 16/512"
                        className="h-8"
                        data-testid={`bb-item-desc-${idx}`} />
                    </td>
                    <td className="px-1 py-1">
                      <Input value={it.serial}
                        onChange={(e) => updateItem(idx, "serial", e.target.value)}
                        className="h-8 font-mono" />
                    </td>
                    <td className="px-1 py-1">
                      <Select value={it.condition}
                        onValueChange={(v) => updateItem(idx, "condition", v)}>
                        <SelectTrigger className="h-8 w-20"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {["A", "B", "C", "SCRAP"].map((c) =>
                            <SelectItem key={c} value={c}>{c}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-1 py-1">
                      <Input type="number" min="1" value={it.quantity}
                        onChange={(e) => updateItem(idx, "quantity", e.target.value)}
                        className="h-8 text-right" />
                    </td>
                    <td className="px-1 py-1">
                      <Input type="number" min="0" value={it.proposed_amount}
                        onChange={(e) => updateItem(idx, "proposed_amount", e.target.value)}
                        className="h-8 text-right"
                        data-testid={`bb-item-amount-${idx}`} />
                    </td>
                    <td className="px-1 py-1 text-center">
                      {items.length > 1 && (
                        <button onClick={() => setItems((arr) => arr.filter((_, i) => i !== idx))}
                          className="text-slate-400 hover:text-rose-700">
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-slate-50">
                <tr>
                  <td colSpan={4} className="px-2 py-1.5 text-right text-[11px] uppercase text-slate-500 tracking-wider">
                    Total proposed
                  </td>
                  <td className="px-2 py-1.5 text-right font-semibold text-slate-900">
                    {fmt(total)}
                  </td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
          <div>
            <Button variant="outline" size="sm" className="h-8"
              onClick={() => setItems((a) => [...a, { ...EMPTY_LINE }])}
              data-testid="buyback-add-line">
              <Plus className="h-3.5 w-3.5 mr-1.5" /> Add line
            </Button>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.12em] text-slate-500 mb-1">Notes</div>
            <Input value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} data-testid="buyback-create-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving}
            className="bg-slate-900 text-white hover:bg-slate-800"
            data-testid="buyback-create-save">
            {saving ? "Saving…" : "Create draft"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/* ---------------------------------------------------- Approve dialog */
function ApproveBuybackDialog({ buyback, onCancel, onDone }) {
  const [items, setItems] = useState(() =>
    (buyback.items || []).map((it) => ({ ...it,
      approved_amount: it.approved_amount ?? it.proposed_amount })));
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const total = useMemo(() =>
    items.reduce((s, i) => s + (Number(i.approved_amount) || 0) * (Number(i.quantity) || 1), 0),
    [items]);

  const updateItem = (idx, key, val) => {
    setItems((arr) => arr.map((it, i) => i === idx ? { ...it, [key]: val } : it));
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/buybacks/${buyback.id}/approve`, {
        approved_items: items.map((it) => ({
          description: it.description,
          serial: it.serial || null,
          category: it.category || null,
          condition: it.condition || "B",
          quantity: Number(it.quantity) || 1,
          proposed_amount: Number(it.proposed_amount) || 0,
          approved_amount: Number(it.approved_amount) || 0,
          notes: it.notes || null,
        })),
        approval_notes: notes || null,
      });
      toast.success(`Approved ${fmt(total)}`);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Approve failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onCancel()}>
      <DialogContent className="max-w-3xl" data-testid="buyback-approve-dialog">
        <DialogHeader><DialogTitle>Approve buyback {buyback.code}</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div className="text-[12px] text-slate-500">
            Adjust the approved amount per item.  Approval creates a claim; issuing credit posts it to the ledger.
          </div>
          <div className="border border-slate-200 rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[10px] uppercase text-slate-500 tracking-wider">
                <tr>
                  <th className="text-left px-2 py-1.5">Item</th>
                  <th className="text-left px-2 py-1.5">Cond</th>
                  <th className="text-right px-2 py-1.5">Qty</th>
                  <th className="text-right px-2 py-1.5">Proposed</th>
                  <th className="text-right px-2 py-1.5 w-36">Approved (₹)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.map((it, idx) => (
                  <tr key={idx}>
                    <td className="px-2 py-1.5">{it.description}</td>
                    <td className="px-2 py-1.5">{it.condition}</td>
                    <td className="px-2 py-1.5 text-right">{it.quantity}</td>
                    <td className="px-2 py-1.5 text-right text-slate-500">{fmt(it.proposed_amount)}</td>
                    <td className="px-1 py-1">
                      <Input type="number" min="0" value={it.approved_amount}
                        onChange={(e) => updateItem(idx, "approved_amount", e.target.value)}
                        className="h-8 text-right"
                        data-testid={`bb-approve-amount-${idx}`} />
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-slate-50">
                <tr>
                  <td colSpan={4} className="px-2 py-1.5 text-right text-[11px] uppercase text-slate-500 tracking-wider">
                    Total approved
                  </td>
                  <td className="px-2 py-1.5 text-right font-semibold text-emerald-700">
                    {fmt(total)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.12em] text-slate-500 mb-1">Approval notes</div>
            <Input value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>Cancel</Button>
          <Button onClick={save} disabled={saving}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="buyback-approve-save">
            {saving ? "Approving…" : `Approve ${fmt(total)}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/* ---------------------------------------------------- Apply credit panel */
function ApplyCreditPanel({ masters, isSuntronix, defaultMc, onDone }) {
  const [mc, setMc] = useState(defaultMc);
  const [amount, setAmount] = useState("");
  const [invoiceId, setInvoiceId] = useState("");
  const [statementId, setStatementId] = useState("");
  const [description, setDescription] = useState("");
  const [balance, setBalance] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadBalance = useCallback(async (id) => {
    if (!id) { setBalance(null); return; }
    try {
      const { data } = await api.get(`/buybacks/credit-balance/${id}`);
      setBalance(data.balance);
    } catch { setBalance(null); }
  }, []);

  useEffect(() => { loadBalance(mc); }, [mc, loadBalance]);

  const apply = async () => {
    if (!mc || !amount || Number(amount) <= 0) {
      toast.error("Pick customer + enter amount"); return;
    }
    if (!invoiceId && !statementId) {
      toast.error("Enter an invoice or statement id"); return;
    }
    setBusy(true);
    try {
      await api.post("/buybacks/apply-credit", {
        master_company_id: mc,
        amount: Number(amount),
        currency: "INR",
        invoice_id: invoiceId || null,
        statement_id: statementId || null,
        description: description || null,
      });
      setAmount(""); setInvoiceId(""); setStatementId(""); setDescription("");
      loadBalance(mc);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Apply failed");
    } finally { setBusy(false); }
  };

  if (!isSuntronix) {
    return (
      <div className="bg-white border border-slate-200 rounded-md p-8 text-center text-sm text-slate-500">
        Only Suntronix finance can apply credits.  Contact your account manager to offset an invoice.
      </div>
    );
  }

  return (
    <div className="bg-white border border-slate-200 rounded-md p-4 max-w-2xl space-y-3"
      data-testid="apply-credit-panel">
      <div className="flex items-center gap-2">
        <ArrowLeftRight className="h-4 w-4 text-slate-500" />
        <div className="text-sm font-medium text-slate-800">Apply customer credit to an invoice</div>
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-[0.12em] text-slate-500 mb-1">Customer *</div>
        <Select value={mc} onValueChange={setMc}>
          <SelectTrigger className="h-9" data-testid="apply-mc-select">
            <SelectValue placeholder="Select customer…" />
          </SelectTrigger>
          <SelectContent>
            {masters.map((m) => (
              <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {balance !== null && (
          <div className="text-[12px] text-slate-500 mt-1">
            Available: <span className="font-semibold text-emerald-700">{fmt(balance)}</span>
          </div>
        )}
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.12em] text-slate-500 mb-1">Amount *</div>
          <Input type="number" min="0" value={amount}
            onChange={(e) => setAmount(e.target.value)}
            data-testid="apply-amount" />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.12em] text-slate-500 mb-1">Description</div>
          <Input value={description} onChange={(e) => setDescription(e.target.value)} />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.12em] text-slate-500 mb-1">Invoice ID</div>
          <Input value={invoiceId} onChange={(e) => setInvoiceId(e.target.value)}
            placeholder="INV-2026-…" className="font-mono"
            data-testid="apply-invoice-id" />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.12em] text-slate-500 mb-1">Statement ID</div>
          <Input value={statementId} onChange={(e) => setStatementId(e.target.value)}
            placeholder="MS-…" className="font-mono" />
        </div>
      </div>
      <div className="pt-2">
        <Button onClick={apply} disabled={busy || !mc}
          className="h-9 bg-indigo-600 hover:bg-indigo-700 text-white"
          data-testid="apply-credit-save">
          <FileText className="h-4 w-4 mr-1.5" strokeWidth={1.6} />
          {busy ? "Applying…" : "Apply credit"}
        </Button>
      </div>
    </div>
  );
}
